import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { BookA, Plus, Trash2, Save, ClipboardPaste } from "lucide-react";
import { API } from "../App";

const parseBulk = (text) => text.split("\n").map((l) => l.match(/^\s*([^:=\-–—\t]{1,40}?)\s*(?:[:=\t]|\s[-–—]\s)\s*(.+)$/)).filter(Boolean).map((m) => ({ abbr: m[1].trim(), meaning: m[2].trim() }));

const GlossaryCard = () => {
  const [rows, setRows] = useState([]);
  const [saved, setSaved] = useState("[]");
  const [draft, setDraft] = useState({ abbr: "", meaning: "" });
  const [bulk, setBulk] = useState(null);
  const [saving, setSaving] = useState(false);
  const [max, setMax] = useState(200);

  useEffect(() => {
    axios.get(`${API}/settings/report-prompts`, { withCredentials: true }).then((r) => {
      setRows(r.data.glossary || []); setSaved(JSON.stringify(r.data.glossary || [])); setMax(r.data.max_glossary || 200);
    }).catch(() => {});
  }, []);

  const dirty = JSON.stringify(rows) !== saved;

  const add = () => {
    const a = draft.abbr.trim(), m = draft.meaning.trim();
    if (!a || !m) return;
    if (rows.some((r) => r.abbr.toLowerCase() === a.toLowerCase())) { toast.error(`« ${a} » existe déjà`); return; }
    setRows([...rows, { abbr: a, meaning: m }]); setDraft({ abbr: "", meaning: "" });
  };

  const importBulk = () => {
    const parsed = parseBulk(bulk || "");
    if (!parsed.length) { toast.error("Aucune ligne reconnue (format : ABC : signification)"); return; }
    const existing = new Set(rows.map((r) => r.abbr.toLowerCase()));
    const added = parsed.filter((p) => !existing.has(p.abbr.toLowerCase()) && existing.add(p.abbr.toLowerCase()));
    setRows([...rows, ...added]); setBulk(null);
    toast.success(`${added.length} abréviation(s) ajoutée(s)`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/settings/report-prompts`, { glossary: rows }, { withCredentials: true });
      setRows(r.data.glossary); setSaved(JSON.stringify(r.data.glossary));
      toast.success("Glossaire enregistré");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="glossary-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2"><BookA className="w-5 h-5" /> Glossaire personnel</CardTitle>
        <p className="text-sm text-muted-foreground">Vos abréviations cliniques (ex. « TAG », « EMDR », « RC ») sont transmises à l'IA qui les développe correctement dans chaque compte rendu.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.length > 0 && (
          <div className="rounded-lg border divide-y max-h-72 overflow-y-auto" data-testid="glossary-list">
            {rows.map((r, i) => (
              <div key={r.abbr} className="grid grid-cols-[110px_1fr_auto] gap-2 items-center px-3 py-1.5 text-sm" data-testid={`glossary-row-${i}`}>
                <span className="font-mono font-semibold text-primary truncate">{r.abbr}</span>
                <span className="text-foreground/90 truncate">{r.meaning}</span>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`glossary-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-[110px_1fr_auto] gap-2 items-center">
          <Input value={draft.abbr} onChange={(e) => setDraft({ ...draft, abbr: e.target.value })} placeholder="TAG" maxLength={40} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="glossary-abbr-input" />
          <Input value={draft.meaning} onChange={(e) => setDraft({ ...draft, meaning: e.target.value })} placeholder="Trouble anxieux généralisé" maxLength={200} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="glossary-meaning-input" />
          <Button variant="outline" size="sm" onClick={add} disabled={!draft.abbr.trim() || !draft.meaning.trim() || rows.length >= max} data-testid="glossary-add-btn"><Plus className="w-4 h-4" /></Button>
        </div>
        {bulk === null ? (
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => setBulk("")} data-testid="glossary-bulk-toggle"><ClipboardPaste className="w-3.5 h-3.5 mr-1.5" /> Coller une liste (une ligne par abréviation : « ABC : signification »)</Button>
        ) : (
          <div className="space-y-2">
            <textarea value={bulk} onChange={(e) => setBulk(e.target.value)} rows={5} placeholder={"TAG : Trouble anxieux généralisé\nRC = Restructuration cognitive\nEMDR - Désensibilisation et retraitement par les mouvements oculaires"} className="w-full px-3 py-2 text-sm font-mono border rounded-lg bg-background border-border" data-testid="glossary-bulk-textarea" />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setBulk(null)}>Annuler</Button>
              <Button variant="outline" size="sm" onClick={importBulk} data-testid="glossary-bulk-import-btn">Ajouter les lignes</Button>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{rows.length} / {max} entrées</span>
          <Button onClick={save} disabled={saving || !dirty} className="gap-2" data-testid="save-glossary-btn"><Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer le glossaire"}</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default GlossaryCard;
